/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import type { SortOption } from '@/lib/search/sort-options';
import { useSortSelection } from './use-sort-selection';

/**
 * BG's MOBILE sort control: the `Sort` segment of the bottom `Filters │ Sort`
 * pill, opening live BG's own full-screen `SORT` takeover.
 *
 * WHAT THIS REPLACES. Until 2026-08-23 the Sort segment had a transparent
 * native `<select>` stretched over it. `bg-filter-sort-bar.tsx` recorded why —
 * live's control opens a modal this storefront had no counterpart for — and
 * `theme/tokens/brand-bg/search.css` recorded the spec with the closing line
 * *"Retired by building that overlay."* This is that build. The owner's report
 * is what made it urgent: *"our sort popup is a crazy tiny desktop thing we
 * need this full screen mobile thing"*. A native select's popup is drawn by the
 * OS, so it was not merely unstyled — it was structurally unreachable, and only
 * replacing the control could fix it.
 *
 * TWO SOURCES AGREE, AND THEY WERE OBTAINED INDEPENDENTLY.
 *
 * (1) DECLARED — live's own shipped stylesheet, transcribed in full in
 *     `theme/tokens/brand-bg/search.css`, read out of the mobile dump
 *     `.reference/dumps/bg-search-m-440/mob-search-440.html`:
 *
 *       .mobile-overlay__pane   position:fixed; inset:0; background:#fff;
 *                               z-index:1010
 *       header                  30px tall, centred, margin-top:24px, holding an
 *                               uppercase h2 at 16px / 1.88 / ls .16px
 *       close                   30x30 at the LEADING edge, padding-left:16px
 *       .sort-container <=767   height:auto; margin-top:15px; padding:0 0 24px 16px
 *       ul li                   letter-spacing:.2px; margin:37px 0 0
 *       ul li .active           border-bottom:1px solid #000; padding-bottom:2px
 *
 * (2) MEASURED — the owner's DevTools capture
 *     `.reference/owner-bg-mobile-2026-08-23/Screenshot 2026-08-23 at 12.00.26 PM.png`,
 *     whose device toolbar is IN FRAME stating `iPhone 12 Pro, 390 x 844, 100%`
 *     on `bergdorfgoodman.com/search/?…`. So the width is declared by the
 *     capture rather than inferred from its pixels — and the white pane
 *     measures 780 x 1688 device px, i.e. exactly 2.000 px/CSS and exactly
 *     390 x 844 CSS. FULL VIEWPORT, confirming `inset:0` rather than resembling
 *     it. Sampled through a colour-managed canvas:
 *
 *       header ink        29.0 CSS from the pane's top edge
 *       first option ink  109.5 CSS
 *       option pitch      58.0 CSS, identical across all four rows
 *
 * THE TWO RECONCILE ARITHMETICALLY, which is why both are quoted rather than
 * one being preferred: header `margin-top:24` + header `30` + list
 * `margin-top:15` = 69, plus the first `li`'s own `margin-top:37` = 106 for the
 * row's BOX top, against 109.5 for its first INK — a ~3.5px cap-height offset on
 * a 13px line box. The declared mechanism predicts the measured picture.
 *
 * WHY THE UNDERLINE IS A RECOLOUR AND NOT AN ADDED BORDER. Every option already
 * carries `border-bottom:1px solid #fff` — invisible against the white pane —
 * and selection only changes its COLOUR to #000. Nothing moves. Adding a border
 * on selection instead would shift every row below it by 1px, which is exactly
 * the thing that gets reverse-engineered wrongly from a screenshot.
 *
 * BG ONLY BY MOUNT. This renders inside `BgFilterSortBar`, which the routes
 * mount behind `SearchUx.mobileFilterSortBar` / the PLP's equivalent — true for
 * BG, false for NM — inside a `contents md:hidden` wrapper. NM's mobile sort is
 * a DIFFERENT CONTROL: a ~88.6%-wide LEFT PANEL with the page dimmed beside it,
 * a left-aligned `SORT BY:` with a colon, its close at the TRAILING edge, radio
 * marks instead of underlines, and a fifth option BG's list does not have. It
 * lives in `nm-mobile-sort-overlay.tsx`. These are two builds, not one
 * component with a brand flag — they share no band.
 *
 * ACCESSIBILITY COMES FROM `Sheet`. It is this repo's Radix dialog wrapper and
 * already carries the focus trap, Escape-to-close, focus restoration to the
 * trigger, the scroll lock, and `aria-hidden` on everything behind the portal.
 *
 * IT DOES NOT SET `aria-modal` — verified by dumping the rendered attributes,
 * which give `role="dialog"` and `aria-labelledby` and no `aria-modal`. Radix
 * hides the background instead: every body child but the portal gains
 * `aria-hidden="true"`, which removes them from the accessibility tree outright
 * rather than merely advising against them. The guarantee is real; a test
 * asserting `aria-modal` would redden against a correct implementation.
 *
 * `bg-search-panel.tsx` — BG's full-screen header search takeover — is the
 * precedent for driving it this way, `closeButton={null}` included, because
 * live's close is at the leading edge and the stock one is not.
 *
 * FOCUS IS RESTORED EXPLICITLY, NOT LEFT TO RADIX. Radix returns focus to
 * whatever held it before the pane opened — which is nothing here. On macOS and
 * on touch, clicking a `<button>` does not focus it, so at the moment of
 * opening `document.activeElement` is the body and there is nothing to restore
 * TO. Measured on the running app under iPhone 12 Pro emulation before this
 * was added: after Escape, `document.activeElement` was the body and the
 * shopper's place in the page was gone. `onCloseAutoFocus` is therefore
 * intercepted and the trigger focused by hand, which is correct on every
 * platform rather than only on the ones where a click happens to focus.
 *
 * `side="left"` WITH A FULL-WIDTH PANE, rather than a centred `Dialog`. The
 * measured pane is `inset:0`, which both express; the sheet is chosen because
 * it slides from the leading edge, which is the transition the same brand's
 * search takeover already uses, and because `Dialog`'s own content arm is built
 * around a centred, radius-bearing, max-width card that would have to be
 * unpicked class by class.
 */
export default function BgMobileSortOverlay({
    serverOptions,
    fallbackOptions,
}: {
    serverOptions?: Array<{ id?: string; label?: string }>;
    /** The brand's curated list — named by the caller, never defaulted. */
    fallbackOptions: readonly SortOption[];
}): ReactElement {
    const { t } = useTranslation('search');
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const { options, selected, labelOf, choose, isPending } = useSortSelection(serverOptions, fallbackOptions);

    // The pill segment reads "Sort"; the pane's own title is "SORT", uppercased
    // by the stylesheet rather than by the string, so the same key serves both
    // and a translation cannot lose the casing rule.
    const sortLabel = t('bgSortSegment', { defaultValue: 'Sort' });

    return (
        <>
            <button
                type="button"
                ref={triggerRef}
                data-testid="bg-filter-sort-bar-sort"
                aria-haspopup="dialog"
                aria-expanded={open}
                onClick={() => setOpen(true)}
                className={cn(
                    // Live's Sort is a <button>, which centres its own text in
                    // the 26px box. Same box, same 26px as the span this
                    // replaced — the element type now supplies the centring
                    // that had to be added by hand before.
                    'focus-ring-button flex h-(--bg-srp-pill-sort-height) w-full cursor-pointer items-center justify-center px-(--bg-srp-pill-sort-pad-inline) text-center',
                    isPending && 'pointer-events-none opacity-50 transition-opacity'
                )}>
                {sortLabel}
            </button>

            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent
                    side="left"
                    data-testid="bg-mobile-sort-overlay"
                    /* `w-full max-w-none sm:max-w-none` is live's `inset:0`, and
                       the responsive arm is not redundant. `SheetContent`'s
                       `side="left"` arm ends in `sm:max-w-sm`, which caps the
                       pane at 384px from 640 up — inside the mobile tier this
                       serves, since `md` is 768. Without cancelling it the
                       takeover silently stops being one between 640 and 767.

                       `max-w-none` ALONE IS NOT ENOUGH, verified by dumping the
                       rendered class list: tailwind-merge treats a base utility
                       and its `sm:` variant as different properties, so both
                       survive and the variant still wins above 640. A `sm:` arm
                       can only be cancelled by another `sm:` arm. */
                    className="bg-srp-sort-overlay w-full max-w-none sm:max-w-none gap-0 border-0 p-0 [--ui-shadow:none]"
                    overlayClassName="bg-srp-sort-overlay-scrim"
                    /* No description exists for this pane — the title and the
                       four rows are the whole content — so Radix's auto-wired
                       `aria-describedby` is opted out of explicitly. Left
                       implicit it ships an IDREF pointing at nothing. */
                    aria-describedby={undefined}
                    onCloseAutoFocus={(event) => {
                        event.preventDefault();
                        triggerRef.current?.focus();
                    }}
                    closeButton={null}>
                    <div className="bg-srp-sort-overlay-header relative flex items-center justify-center">
                        <button
                            type="button"
                            data-testid="bg-mobile-sort-close"
                            onClick={() => setOpen(false)}
                            aria-label={t('sort.closePanel', { defaultValue: 'Close sort options' })}
                            /* `box-content` so the 30px icon box is the CONTENT
                               box and the 16px leading pad sits outside it —
                               live's control is 30 + 16 = 46 wide, with the
                               icon spanning 16..46. Under the project's global
                               `border-box` the same rule made the control 30
                               wide overall, leaving 14px of content for a 30px
                               glyph. The filter pane already does this; the two
                               panes share live's one `.close` rule and must not
                               disagree. */
                            className="focus-ring-button bg-srp-sort-overlay-close absolute start-0 box-content cursor-pointer">
                            <CloseGlyph />
                        </button>
                        <SheetTitle className="bg-srp-sort-overlay-title">{sortLabel}</SheetTitle>
                    </div>

                    <ul
                        role="menu"
                        aria-label={sortLabel}
                        data-testid="bg-mobile-sort-list"
                        className="bg-srp-sort-overlay-list overflow-y-auto">
                        {options.map((option) => {
                            const isSelected = option.id === selected?.id;
                            return (
                                <li key={option.id} role="none">
                                    <button
                                        type="button"
                                        role="menuitemradio"
                                        aria-checked={isSelected}
                                        data-testid="bg-mobile-sort-option"
                                        data-selected={isSelected ? '' : undefined}
                                        onClick={() => {
                                            choose(option.id);
                                            setOpen(false);
                                        }}
                                        className="bg-srp-sort-overlay-option focus-ring-button cursor-pointer text-left">
                                        {labelOf(option)}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </SheetContent>
            </Sheet>
        </>
    );
}

/**
 * The pane's dismiss mark, at the LEADING edge — the opposite side from NM, and
 * the half of this that live's stylesheet states outright (`30x30`,
 * `padding-left:16px`).
 *
 * THE PATH IS NOW LIVE'S OWN, not a reconstruction. This block used to say the
 * geometry could not be recovered from a capture and that the storefront's
 * generic cross had been re-boxed instead. That was true of the capture and
 * false of the evidence available: live serves the mark as a base64 SVG inside
 * `.mobile-overlay__pane .close .close-icon` in its shipped bundle
 * (`.reference/dumps/bg-search-m-440/…/client.<hash>.bundle.css`), so the
 * geometry was one decode away. Decoded verbatim:
 *
 *   <svg width="30" height="30"><g fill-rule="evenodd">
 *     <path d="M6.094 5 5 6.094l18.622 18.623 1.095-1.094z" />
 *     <path d="M23.623 5 5 23.623l1.095 1.094L24.717 6.094z" /></g></svg>
 *
 * It is FILLED and two-stroke, like the drill-down's back mark, not stroked —
 * so its weight (~1.55 CSS) comes from the path rather than a `stroke-width`.
 *
 * WHY THE SIZE CHANGED. The ink spans 5..24.717 of a 30 box, i.e. 19.72 CSS
 * when the box is 30. The previous 18px box painted 10.5 CSS of ink against
 * live's measured 19.5 — the measured INK had been applied as the BOX. Both
 * numbers re-derived here from the owner's own 390x844 capture by scanning the
 * close region and the title region separately: scanned together they merge
 * into one band, which is what made a half-size glyph read as a title offset.
 */
function CloseGlyph(): ReactElement {
    return (
        <svg
            aria-hidden="true"
            focusable="false"
            viewBox="0 0 30 30"
            className="size-(--bg-srp-sort-overlay-close-glyph)">
            <g fillRule="evenodd" fill="currentColor">
                <path d="M6.094 5 5 6.094l18.622 18.623 1.095-1.094z" />
                <path d="M23.623 5 5 23.623l1.095 1.094L24.717 6.094z" />
            </g>
        </svg>
    );
}
